// src/pages/lms/groups/Command.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import { useSocket } from '../../../../hooks/useSocket';
import { formatUtcTimeOnly, getBrowserTimezone } from '../../../../utils/timezoneUtils';
import { 
  Users, Search, CheckSquare, Square, Coffee, Briefcase, Building2, 
  FolderOpen, X, CheckCircle, AlertCircle, ChevronDown, ChevronUp, 
  RefreshCw, Filter, Clock, Check
} from 'lucide-react';

const Command = () => {
  const { currentUser } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL || 'https://ksp3plhq.com/api';
  
  // Socket connection for real-time updates
  const { socket, isConnected } = useSocket(currentUser?.id);
  
  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [groups, setGroups] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState({});
  const [activities, setActivities] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [projects, setProjects] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [activeEmployees, setActiveEmployees] = useState([]);
  
  // Modal states
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [selectedGroupForAction, setSelectedGroupForAction] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState('');
  const [selectedBusinessUnit, setSelectedBusinessUnit] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  
  // Filter state
  const [filterType, setFilterType] = useState('all');
  
  
  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch groups
        const groupsResponse = await fetch(`${API_URL}/work-groups`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!groupsResponse.ok) {
          throw new Error('Failed to fetch groups');
        }
        
        const groupsData = await groupsResponse.json();
        setGroups(groupsData);
        
        // Initialize selected members state
        const initialSelectedMembers = {};
        groupsData.forEach(group => {
          initialSelectedMembers[group.id] = [];
        });
        setSelectedMembers(initialSelectedMembers);
        
        // Fetch activities
        const activitiesResponse = await fetch(`${API_URL}/activities`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!activitiesResponse.ok) {
          throw new Error('Failed to fetch activities');
        }
        
        const activitiesData = await activitiesResponse.json();
        setActivities(activitiesData);
        
        // Fetch business units
        const businessUnitsResponse = await fetch(`${API_URL}/business-units`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!businessUnitsResponse.ok) {
          throw new Error('Failed to fetch business units');
        }
        
        const businessUnitsData = await businessUnitsResponse.json();
        setBusinessUnits(businessUnitsData);
        
        // Fetch projects
        const projectsResponse = await fetch(`${API_URL}/projects`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!projectsResponse.ok) {
          throw new Error('Failed to fetch projects');
        }
        
        const projectsData = await projectsResponse.json();
        setProjects(projectsData);
        
        // Fetch all users
        const usersResponse = await fetch(`${API_URL}/users`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!usersResponse.ok) {
          throw new Error('Failed to fetch users');
        }
        
        const usersData = await usersResponse.json();
        setAllUsers(usersData);
        
        // Fetch active employees
        const activeEmployeesResponse = await fetch(`${API_URL}/labor/active`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!activeEmployeesResponse.ok) {
          throw new Error('Failed to fetch active employees');
        }
        
        const activeEmployeesData = await activeEmployeesResponse.json();
        setActiveEmployees(activeEmployeesData.usersList || []);
        
        // Initialize expanded groups state - only expand groups with active members
        const activeEmployeesList = activeEmployeesData.usersList || [];
        const groupsWithActiveMembers = groupsData.filter(group => {
          if (!Array.isArray(group.members)) return false;
          const groupMembers = group.members.map(id => parseInt(id, 10));
          return groupMembers.some(memberId => 
            activeEmployeesList.some(e => e && e.employee_id === memberId)
          );
        });
        setExpandedGroups(groupsWithActiveMembers.map(group => group.id));
        
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(`Failed to load data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [API_URL]);
  
  // Socket event listeners for real-time updates
  useEffect(() => {
    if (!socket) return;
    
    socket.on('update-clock-status', async (data) => {
      console.log('Received clock status update:', data);
      
      // Refresh active employees
      try {
        const activeEmployeesResponse = await fetch(`${API_URL}/labor/active`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (activeEmployeesResponse.ok) {
          const activeEmployeesData = await activeEmployeesResponse.json();
          setActiveEmployees(activeEmployeesData.usersList || []);
          
          // Update expanded groups state based on new active employee data
          const activeEmployeesList = activeEmployeesData.usersList || [];
          const groupsWithActiveMembers = groups.filter(group => {
            if (!Array.isArray(group.members)) return false;
            const groupMembers = group.members.map(id => parseInt(id, 10));
            return groupMembers.some(memberId => 
              activeEmployeesList.some(e => e && e.employee_id === memberId)
            );
          });
          setExpandedGroups(groupsWithActiveMembers.map(group => group.id));
        }
      } catch (err) {
        console.error('Error refreshing active employees:', err);
      }
    });

    // Handle activity updates
    socket.on('activity-update', (data) => {
      console.log('Received activity update in Command:', data);
      if (data.activity) {
        setActivities(prevActivities => {
          const updatedActivities = prevActivities.map(activity =>
            activity.id === data.activity.id ? data.activity : activity
          );
          return updatedActivities;
        });
      }
    });

    // Handle business unit updates (if needed in the future)
    socket.on('business-unit-update', (data) => {
      console.log('Received business unit update in Command:', data);
      if (data.businessUnit) {
        setBusinessUnits(prevUnits => {
          const updatedUnits = prevUnits.map(unit =>
            unit.id === data.businessUnit.id ? data.businessUnit : unit
          );
          return updatedUnits;
        });
      }
    });
    
    return () => {
      socket.off('update-clock-status');
      socket.off('activity-update');
      socket.off('business-unit-update');
    };
  }, [socket, API_URL]);
  
  // Toggle group expansion
  const toggleGroupExpansion = (groupId) => {
    setExpandedGroups(prev => {
      if (prev.includes(groupId)) {
        return prev.filter(id => id !== groupId);
      } else {
        return [...prev, groupId];
      }
    });
  };
  
  // Toggle member selection
  const toggleMemberSelection = (groupId, memberId) => {
    setSelectedMembers(prev => {
      const groupMembers = prev[groupId] || [];
      
      if (groupMembers.includes(memberId)) {
        return {
          ...prev,
          [groupId]: groupMembers.filter(id => id !== memberId)
        };
      } else {
        return {
          ...prev,
          [groupId]: [...groupMembers, memberId]
        };
      }
    });
  };
  
  // Select all members in a group
  const selectAllMembers = (groupId, members) => {
    setSelectedMembers(prev => {
      // If all are already selected, unselect all
      if (prev[groupId] && prev[groupId].length === members.length) {
        return {
          ...prev,
          [groupId]: []
        };
      } else {
        // Otherwise select all
        return {
          ...prev,
          [groupId]: [...members]
        };
      }
    });
  };
  
  // Handle Break action for selected members
  const handleBreak = async (groupId) => {
    if (!selectedMembers[groupId] || selectedMembers[groupId].length === 0) {
      setError("Please select at least one member");
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const token = localStorage.getItem('token');
      const membersToUpdate = selectedMembers[groupId];
      const group = groups.find(g => g.id === groupId);
      
      // For each selected member
      for (const memberId of membersToUpdate) {
        // Check if the employee is currently clocked in
        const currentSessionResponse = await fetch(`${API_URL}/labor/current/${memberId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!currentSessionResponse.ok) {
          throw new Error(`Failed to check current session for user ${memberId}`);
        }
        
        const currentSessionData = await currentSessionResponse.json();
        
        if (currentSessionData.active && currentSessionData.entry) {
          const session = currentSessionData.entry;
          
          // Check if already on break
          if (session.activity === 'BREAK') {
            // End break - clock out current session
            await fetch(`${API_URL}/labor/${session.id}/clockout`, {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            
            // Get previous activity for this employee
            const prevActivityResponse = await fetch(`${API_URL}/labor/previous-activity/${memberId}`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            
            let activity = 'IDLE';
            let business_unit = 'IDLE';
            let project = null;
            
            if (prevActivityResponse.ok) {
              const prevActivity = await prevActivityResponse.json();
              activity = prevActivity.activity;
              business_unit = prevActivity.business_unit;
              project = prevActivity.project;
            }
            
            // Clock in with restored or IDLE activity
            await fetch(`${API_URL}/labor`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                employee_id: memberId,
                full_name: findUserName(memberId),
                activity: activity,
                business_unit: business_unit,
                project: project,
                group: group?.name || null,
                shift: null,
                team: null
              })
            });
          } else {
            // Start break - clock out current session
            await fetch(`${API_URL}/labor/${session.id}/clockout`, {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            
            // Clock in with BREAK, keeping current business unit
            await fetch(`${API_URL}/labor`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                employee_id: memberId,
                full_name: findUserName(memberId),
                activity: 'Break',
                business_unit: session.business_unit,
                group: group?.name || null,
                project: session.project,
                shift: session.shift,
                team: session.team
              })
            });
          }
        }
      }
      
      setSuccess(`${membersToUpdate.length} member(s) break status updated successfully`);
      
      // Clear selection
      setSelectedMembers(prev => ({
        ...prev,
        [groupId]: []
      }));
      
      // Refresh active employees
      const activeEmployeesResponse = await fetch(`${API_URL}/labor/active`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (activeEmployeesResponse.ok) {
        const activeEmployeesData = await activeEmployeesResponse.json();
        setActiveEmployees(activeEmployeesData.usersList || []);
        
        // Update expanded groups state based on new active employee data
        const activeEmployeesList = activeEmployeesData.usersList || [];
        const groupsWithActiveMembers = groups.filter(group => {
          if (!Array.isArray(group.members)) return false;
          const groupMembers = group.members.map(id => parseInt(id, 10));
          return groupMembers.some(memberId => 
            activeEmployeesList.some(e => e && e.employee_id === memberId)
          );
        });
        setExpandedGroups(groupsWithActiveMembers.map(group => group.id));
      }
      
    } catch (err) {
      console.error('Error handling break:', err);
      setError(`Failed to update break status: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle Lunch action for selected members
  const handleLunch = async (groupId) => {
    if (!selectedMembers[groupId] || selectedMembers[groupId].length === 0) {
      setError("Please select at least one member");
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const token = localStorage.getItem('token');
      const membersToUpdate = selectedMembers[groupId];
      const group = groups.find(g => g.id === groupId);
      
      // For each selected member
      for (const memberId of membersToUpdate) {
        // Check if the employee is currently clocked in
        const currentSessionResponse = await fetch(`${API_URL}/labor/current/${memberId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!currentSessionResponse.ok) {
          throw new Error(`Failed to check current session for user ${memberId}`);
        }
        
        const currentSessionData = await currentSessionResponse.json();
        
        if (currentSessionData.active && currentSessionData.entry) {
          const session = currentSessionData.entry;
          
          // Check if already on lunch
          if (session.activity === 'LUNCH') {
            // End lunch - clock out current session
            await fetch(`${API_URL}/labor/${session.id}/clockout`, {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            
            // Get previous activity for this employee
            const prevActivityResponse = await fetch(`${API_URL}/labor/previous-activity/${memberId}`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            
            let activity = 'IDLE';
            let business_unit = 'IDLE';
            let project = null;
            
            if (prevActivityResponse.ok) {
              const prevActivity = await prevActivityResponse.json();
              activity = prevActivity.activity;
              business_unit = prevActivity.business_unit;
              project = prevActivity.project;
            }
            
            // Clock in with restored or IDLE activity
            await fetch(`${API_URL}/labor`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                employee_id: memberId,
                full_name: findUserName(memberId),
                activity: activity,
                business_unit: business_unit,
                project: project,
                group: group?.name || null,
                shift: null,
                team: null
              })
            });
          } else {
            // Start lunch - clock out current session
            await fetch(`${API_URL}/labor/${session.id}/clockout`, {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            
            // Clock in with LUNCH, keeping current business unit
            await fetch(`${API_URL}/labor`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                employee_id: memberId,
                full_name: findUserName(memberId),
                activity: 'Lunch',
                business_unit: session.business_unit,
                group: group?.name || null,
                project: session.project,
                shift: session.shift,
                team: session.team
              })
            });
          }
        }
      }
      
      setSuccess(`${membersToUpdate.length} member(s) lunch status updated successfully`);
      
      // Clear selection
      setSelectedMembers(prev => ({
        ...prev,
        [groupId]: []
      }));
      
      // Refresh active employees
      const activeEmployeesResponse = await fetch(`${API_URL}/labor/active`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (activeEmployeesResponse.ok) {
        const activeEmployeesData = await activeEmployeesResponse.json();
        setActiveEmployees(activeEmployeesData.usersList || []);
        
        // Update expanded groups state based on new active employee data
        const activeEmployeesList = activeEmployeesData.usersList || [];
        const groupsWithActiveMembers = groups.filter(group => {
          if (!Array.isArray(group.members)) return false;
          const groupMembers = group.members.map(id => parseInt(id, 10));
          return groupMembers.some(memberId => 
            activeEmployeesList.some(e => e && e.employee_id === memberId)
          );
        });
        setExpandedGroups(groupsWithActiveMembers.map(group => group.id));
      }
      
    } catch (err) {
      console.error('Error handling lunch:', err);
      setError(`Failed to update lunch status: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Open Modify modal for a group
  const openModifyModal = (groupId) => {
    if (!selectedMembers[groupId] || selectedMembers[groupId].length === 0) {
      setError("Please select at least one member");
      return;
    }
    
    setSelectedGroupForAction(groupId);
    setSelectedActivity('');
    setSelectedBusinessUnit('');
    setSelectedProject('');
    setShowModifyModal(true);
  };
  
  // Handle Modify assignment (Activity, Business Unit, and Project)
  const handleModifyAssignment = async () => {
    if (!selectedGroupForAction) {
      setError("No group selected");
      return;
    }
    
    // Check if at least one field is selected
    if (!selectedActivity && !selectedBusinessUnit && !selectedProject) {
      setError("Please select at least one field to modify (Activity, Business Unit, or Project)");
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const token = localStorage.getItem('token');
      const membersToUpdate = selectedMembers[selectedGroupForAction];
      const group = groups.find(g => g.id === selectedGroupForAction);
      
      // For each selected member
      for (const memberId of membersToUpdate) {
        // Check if the employee is currently clocked in
        const currentSessionResponse = await fetch(`${API_URL}/labor/current/${memberId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!currentSessionResponse.ok) {
          throw new Error(`Failed to check current session for user ${memberId}`);
        }
        
        const currentSessionData = await currentSessionResponse.json();
        
        if (currentSessionData.active && currentSessionData.entry) {
          const session = currentSessionData.entry;
          
          // Don't update if on break or lunch
          if (session.activity === 'BREAK' || session.activity === 'LUNCH') {
            continue;
          }
          
          // Clock out current session
          await fetch(`${API_URL}/labor/${session.id}/clockout`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          // Clock in with updated fields (use selected values or keep current ones)
          await fetch(`${API_URL}/labor`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              employee_id: memberId,
              full_name: findUserName(memberId),
              activity: selectedActivity || session.activity,
              business_unit: selectedBusinessUnit || session.business_unit,
              group: group?.name || null,
              project: selectedProject || session.project,
              shift: session.shift,
              team: session.team
            })
          });
        }
      }
      
      // Build success message based on what was updated
      let updatedFields = [];
      if (selectedActivity) updatedFields.push('activity');
      if (selectedBusinessUnit) updatedFields.push('business unit');
      if (selectedProject) updatedFields.push('project');
      
      setSuccess(`${membersToUpdate.length} member(s) ${updatedFields.join(', ')} updated successfully`);
      
      // Clear selection and close modal
      setSelectedMembers(prev => ({
        ...prev,
        [selectedGroupForAction]: []
      }));
      setShowModifyModal(false);
      
      // Refresh active employees
      const activeEmployeesResponse = await fetch(`${API_URL}/labor/active`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (activeEmployeesResponse.ok) {
        const activeEmployeesData = await activeEmployeesResponse.json();
        setActiveEmployees(activeEmployeesData.usersList || []);
        
        // Update expanded groups state based on new active employee data
        const activeEmployeesList = activeEmployeesData.usersList || [];
        const groupsWithActiveMembers = groups.filter(group => {
          if (!Array.isArray(group.members)) return false;
          const groupMembers = group.members.map(id => parseInt(id, 10));
          return groupMembers.some(memberId => 
            activeEmployeesList.some(e => e && e.employee_id === memberId)
          );
        });
        setExpandedGroups(groupsWithActiveMembers.map(group => group.id));
      }
      
    } catch (err) {
      console.error('Error handling modify assignment:', err);
      setError(`Failed to update fields: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle Remove from Project - removes all selected users from their current projects
  const handleRemoveFromProject = async () => {
    if (!selectedGroupForAction) {
      setError("No group selected");
      return;
    }

    if (!selectedMembers[selectedGroupForAction] || selectedMembers[selectedGroupForAction].length === 0) {
      setError("Please select at least one member to remove from project");
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const token = localStorage.getItem('token');
      const membersToUpdate = selectedMembers[selectedGroupForAction];
      const group = groups.find(g => g.id === selectedGroupForAction);
      
      // For each selected member
      for (const memberId of membersToUpdate) {
        // Check if the employee is currently clocked in
        const currentSessionResponse = await fetch(`${API_URL}/labor/current/${memberId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!currentSessionResponse.ok) {
          throw new Error(`Failed to check current session for user ${memberId}`);
        }
        
        const currentSessionData = await currentSessionResponse.json();
        
        if (currentSessionData.active && currentSessionData.entry) {
          const session = currentSessionData.entry;
          
          // Don't update if on break or lunch
          if (session.activity === 'BREAK' || session.activity === 'LUNCH') {
            continue;
          }
          
          // Clock out current session
          await fetch(`${API_URL}/labor/${session.id}/clockout`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          // Clock in with same activity and business unit but project set to null
          await fetch(`${API_URL}/labor`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              employee_id: memberId,
              full_name: findUserName(memberId),
              activity: session.activity,
              business_unit: session.business_unit,
              group: group?.name || null,
              project: null, // Remove project assignment
              shift: session.shift,
              team: session.team
            })
          });
        }
      }
      
      setSuccess(`${membersToUpdate.length} member(s) removed from projects successfully`);
      
      // Clear selection and close modal
      setSelectedMembers(prev => ({
        ...prev,
        [selectedGroupForAction]: []
      }));
      setShowModifyModal(false);
      
      // Refresh active employees
      const activeEmployeesResponse = await fetch(`${API_URL}/labor/active`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (activeEmployeesResponse.ok) {
        const activeEmployeesData = await activeEmployeesResponse.json();
        setActiveEmployees(activeEmployeesData.usersList || []);
        
        // Update expanded groups state based on new active employee data
        const activeEmployeesList = activeEmployeesData.usersList || [];
        const groupsWithActiveMembers = groups.filter(group => {
          if (!Array.isArray(group.members)) return false;
          const groupMembers = group.members.map(id => parseInt(id, 10));
          return groupMembers.some(memberId => 
            activeEmployeesList.some(e => e && e.employee_id === memberId)
          );
        });
        setExpandedGroups(groupsWithActiveMembers.map(group => group.id));
      }
      
    } catch (err) {
      console.error('Error removing from project:', err);
      setError(`Failed to remove from projects: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Refresh active employees
  const refreshActiveEmployees = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const activeEmployeesResponse = await fetch(`${API_URL}/labor/active`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!activeEmployeesResponse.ok) {
        throw new Error('Failed to refresh active employees');
      }
      
      const activeEmployeesData = await activeEmployeesResponse.json();
      setActiveEmployees(activeEmployeesData.usersList || []);
      
      // Update expanded groups state based on new active employee data
      const activeEmployeesList = activeEmployeesData.usersList || [];
      const groupsWithActiveMembers = groups.filter(group => {
        if (!Array.isArray(group.members)) return false;
        const groupMembers = group.members.map(id => parseInt(id, 10));
        return groupMembers.some(memberId => 
          activeEmployeesList.some(e => e && e.employee_id === memberId)
        );
      });
      setExpandedGroups(groupsWithActiveMembers.map(group => group.id));
      
      setSuccess('Employee data refreshed successfully');
      
      // Clear success message after delay
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
      
    } catch (err) {
      console.error('Error refreshing active employees:', err);
      setError(`Failed to refresh data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Helper function to find user name
  const findUserName = (userId) => {
    const user = allUsers.find(u => u.id === parseInt(userId, 10));
    return user ? (user.full_name || user.username) : `User ${userId}`;
  };
  
  // Helper function to find active employee data for a member
  const getMemberActiveData = (memberId) => {
    return activeEmployees.find(employee => 
      employee && employee.employee_id === parseInt(memberId, 10)
    );
  };

  // Helper function to get active members for a group
  const getActiveMembers = (group) => {
    if (!Array.isArray(group.members)) return [];
    const groupMembers = group.members.map(id => parseInt(id, 10));
    return groupMembers.filter(memberId => 
      activeEmployees.some(e => e && e.employee_id === memberId)
    );
  };
  
  // Helper function to check if all members in a group are selected
  const areAllMembersSelected = (groupId, members) => {
    return (
      selectedMembers[groupId] && 
      members.length > 0 && 
      selectedMembers[groupId].length === members.length
    );
  };
  
  // Helper function to check if selected members are on break/lunch
  const getSelectedMembersBreakLunchState = (groupId) => {
    if (!selectedMembers[groupId] || selectedMembers[groupId].length === 0) {
      return { onBreak: false, onLunch: false, hasSelected: false };
    }
    
    const selectedMemberIds = selectedMembers[groupId];
    let onBreakCount = 0;
    let onLunchCount = 0;
    
    selectedMemberIds.forEach(memberId => {
      const activeData = getMemberActiveData(memberId);
      if (activeData) {
        if (activeData.activity === 'BREAK') onBreakCount++;
        if (activeData.activity === 'LUNCH') onLunchCount++;
      }
    });
    
    return {
      onBreak: onBreakCount > 0,
      onLunch: onLunchCount > 0,
      hasSelected: true,
      allOnBreak: onBreakCount === selectedMemberIds.length,
      allOnLunch: onLunchCount === selectedMemberIds.length
    };
  };
  
  // Filter and sort groups based on search term and active member count
  const filteredGroups = groups.filter(group => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    const nameMatch = group.name && group.name.toLowerCase().includes(searchLower);
    
    const memberMatch = Array.isArray(group.members) && group.members.some(memberId => {
      const user = allUsers.find(u => u.id === parseInt(memberId, 10));
      return user && ((user.full_name && user.full_name.toLowerCase().includes(searchLower)) || 
                     (user.username && user.username.toLowerCase().includes(searchLower)));
    });
    
    return nameMatch || memberMatch;
  }).sort((a, b) => {
    // Sort by active member count (descending) - groups with most active users first
    const aActiveCount = getActiveMembers(a).length;
    const bActiveCount = getActiveMembers(b).length;
    return bActiveCount - aActiveCount;
  });
  
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-slate-200">Group Command Center</h2>
        
        <div className="flex items-center space-x-2">
          {/* Socket Connection Status */}
          <div className={`px-3 py-1 rounded-md inline-flex items-center text-sm ${isConnected ? 'bg-green-900/30 text-green-300' : 'bg-red-900/30 text-red-300'}`}>
            <span className={`w-2 h-2 rounded-full mr-2 ${isConnected ? 'bg-green-400' : 'bg-red-400'}`}></span>
            {isConnected ? 'Connected' : 'Disconnected'} 
          </div>
          
          {/* Refresh Button */}
          <button 
            onClick={refreshActiveEmployees}
            disabled={loading}
            className={`flex items-center px-3 py-1 bg-slate-700 hover:bg-slate-600 
                     text-slate-300 rounded-md transition-colors text-sm ${
                       loading ? 'opacity-50 cursor-not-allowed' : ''
                     }`}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>
      
      {/* Error and Success Messages */}
      {error && (
        <div className="bg-red-900/30 border border-red-800 text-red-300 px-4 py-3 rounded-md flex items-start">
          <AlertCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
          <div>{error}</div>
          <button 
            onClick={() => setError(null)} 
            className="ml-auto text-red-300 hover:text-red-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      
      {success && (
        <div className="bg-green-900/30 border border-green-800 text-green-300 px-4 py-3 rounded-md flex items-start">
          <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
          <div>{success}</div>
          <button 
            onClick={() => setSuccess(null)} 
            className="ml-auto text-green-300 hover:text-green-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      
      {/* Search and Filter Bar */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search groups or members..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 
                     text-sm placeholder-slate-400
                     focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        
        <div className="flex space-x-2">
          <div className="relative">
            <button
              className="flex items-center px-4 py-2 bg-slate-700 hover:bg-slate-600 
                       text-slate-300 rounded-md transition-colors text-sm"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filter
              <ChevronDown className="w-4 h-4 ml-2" />
            </button>
            {/* Filter dropdown would go here */}
          </div>
        </div>
      </div>
      
      {/* Main Content - Group Cards */}
      {loading && !filteredGroups.length ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : !filteredGroups.length ? (
        <div className="text-center py-12 text-slate-400">
          <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p className="text-xl">No groups found</p>
          <p className="text-sm mt-2">Create groups in the Create & Assign section.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredGroups.map(group => {
            // Parse group members
            const groupMembers = Array.isArray(group.members) 
              ? group.members.map(id => parseInt(id, 10))
              : [];
            
            // Get active members count
            const activeMembers = groupMembers.filter(memberId => 
              activeEmployees.some(e => e && e.employee_id === memberId)
            );
            
            const isExpanded = expandedGroups.includes(group.id);
            
            return (
              <div 
                key={group.id} 
                className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm overflow-hidden"
                style={{ borderLeft: `4px solid ${group.color || '#3B82F6'}` }}
              >
                <div className="p-4 border-b border-slate-700 flex justify-between items-center">
                  <div>
                    <h3 className="text-lg font-medium text-slate-200">{group.name}</h3>
                    <div className="flex items-center mt-1 text-sm text-slate-400">
                      <Users className="w-4 h-4 mr-1" />
                      <span>{groupMembers.length} members ({activeMembers.length} active)</span>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => toggleGroupExpansion(group.id)}
                    className="p-1 text-slate-400 hover:text-slate-200 rounded"
                  >
                    {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                </div>
                
                {isExpanded && (
                  <>
                    {/* Action Buttons */}
                    <div className="grid grid-cols-3 gap-1 p-2 bg-slate-700/30">
                      {(() => {
                        const breakLunchState = getSelectedMembersBreakLunchState(group.id);
                        return (
                          <>
                            <button
                              onClick={() => handleBreak(group.id)}
                              className={`flex flex-col items-center justify-center p-2 rounded transition-colors text-xs ${
                                breakLunchState.onBreak
                                  ? 'text-yellow-200 bg-yellow-600/30 hover:bg-yellow-600/50'
                                  : 'text-yellow-300 hover:bg-slate-700'
                              }`}
                            >
                              <Coffee className="w-5 h-5 mb-1" />
                              {breakLunchState.onBreak ? 'End Break' : 'Break'}
                            </button>
                            
                            <button
                              onClick={() => handleLunch(group.id)}
                              className={`flex flex-col items-center justify-center p-2 rounded transition-colors text-xs ${
                                breakLunchState.onLunch
                                  ? 'text-orange-200 bg-orange-600/30 hover:bg-orange-600/50'
                                  : 'text-orange-300 hover:bg-slate-700'
                              }`}
                            >
                              <Coffee className="w-5 h-5 mb-1" />
                              {breakLunchState.onLunch ? 'End Lunch' : 'Lunch'}
                            </button>
                          </>
                        );
                      })()}
                      
                      <button
                        onClick={() => openModifyModal(group.id)}
                        className="flex flex-col items-center justify-center p-2 rounded
                                 text-blue-300 hover:bg-slate-700 transition-colors text-xs"
                      >
                        <Briefcase className="w-5 h-5 mb-1" />
                        Modify
                      </button>
                    </div>
                    
                    {/* Members List */}
                    <div className="p-2">
                      {groupMembers.length === 0 ? (
                        <div className="p-4 text-center text-slate-400">
                          No members in this group
                        </div>
                      ) : (
                        <>
                          {/* Select All Header */}
                          <div 
                            className="flex items-center px-3 py-2 bg-slate-700/50 rounded-md mb-2 cursor-pointer"
                            onClick={() => selectAllMembers(group.id, groupMembers)}
                          >
                            <div className="flex items-center">
                              {areAllMembersSelected(group.id, groupMembers) ? (
                                <CheckSquare className="w-4 h-4 text-blue-400" />
                              ) : (
                                <Square className="w-4 h-4 text-slate-400" />
                              )}
                              <span className="ml-2 text-sm font-medium text-slate-300">Select All</span>
                            </div>
                          </div>
                          
                          {/* Members */}
                          <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                            {groupMembers.sort((a, b) => {
                              // Sort members so active users appear first
                              const aActive = getMemberActiveData(a);
                              const bActive = getMemberActiveData(b);
                              if (aActive && !bActive) return -1; // Active users first
                              if (!aActive && bActive) return 1;
                              return 0; // Maintain original order for same status
                            }).map(memberId => {
                              const user = allUsers.find(u => u.id === memberId);
                              const isSelected = selectedMembers[group.id]?.includes(memberId);
                              const activeData = getMemberActiveData(memberId);
                              const isActive = !!activeData;
                              const isOnBreak = isActive && activeData.activity === 'BREAK';
                              const isOnLunch = isActive && activeData.activity === 'LUNCH';
                              
                              return (
                                <div 
                                  key={`member-${group.id}-${memberId}`}
                                  className={`flex items-center p-3 rounded-md cursor-pointer ${
                                    isSelected ? 'bg-blue-900/20 border border-blue-800' : 'bg-slate-700/30 hover:bg-slate-700/50'
                                  }`}
                                  onClick={() => toggleMemberSelection(group.id, memberId)}
                                >
                                  <div className="flex items-center flex-shrink-0 w-8">
                                    {isSelected ? (
                                      <CheckSquare className="w-4 h-4 text-blue-400" />
                                    ) : (
                                      <Square className="w-4 h-4 text-slate-400" />
                                    )}
                                  </div>
                                  
                                  <div className="ml-2 flex-1 min-w-0">
                                    <div className="flex items-center">
                                      <h4 className="text-sm font-medium text-slate-200 truncate">
                                        {user ? (user.full_name || user.username) : `User ${memberId}`}
                                      </h4>
                                      
                                      {isActive && (
                                        <div 
                                          className={`ml-2 px-1.5 py-0.5 text-xs rounded ${
                                            isOnBreak ? 'bg-yellow-900/50 text-yellow-300' : 
                                            isOnLunch ? 'bg-orange-900/50 text-orange-300' : 
                                            'bg-green-900/50 text-green-300'
                                          }`}
                                        >
                                          {isOnBreak ? 'On Break' : isOnLunch ? 'At Lunch' : 'Active'}
                                        </div>
                                      )}
                                    </div>
                                    
                                    {isActive && (
                                      <div className="flex flex-wrap text-xs text-slate-400 mt-1">
                                        <div className="flex items-center mr-3">
                                          <Clock className="w-3 h-3 mr-1" />
                                          <span>
                                            {(() => {
                                              // Create a proper UTC date and convert to CST/CDT
                                              const utcTime = new Date(activeData.clock_in_time + (activeData.clock_in_time.includes('Z') ? '' : 'Z'));
                                              return utcTime.toLocaleTimeString('en-US', {
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                timeZone: 'America/Chicago',
                                                timeZoneName: 'short'
                                              });
                                            })()}
                                          </span>
                                        </div>
                                        
                                        {!isOnBreak && !isOnLunch && (
                                          <>
                                            <span className="mr-3">
                                              {activeData.activity !== 'IDLE' ? activeData.activity : 'No Activity'}
                                            </span>
                                            <span className="mr-3">
                                              {activeData.business_unit !== 'IDLE' ? activeData.business_unit : 'No BU'}
                                            </span>
                                            <span>
                                              {activeData.project ? activeData.project : 'No Project'}
                                            </span>
                                          </>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
      
      {/* Modify Modal */}
      {showModifyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-lg w-full max-w-md">
            <div className="p-4 border-b border-slate-700 flex justify-between items-center">
              <h3 className="text-lg font-medium text-slate-200">Modify Group Members</h3>
              <button 
                onClick={() => setShowModifyModal(false)}
                className="text-slate-400 hover:text-slate-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                {/* Activity Selection */}
                <div>
                  <label htmlFor="activity" className="block text-sm font-medium text-slate-300 mb-2">
                    Activity (optional)
                  </label>
                  <select
                    id="activity"
                    value={selectedActivity}
                    onChange={(e) => setSelectedActivity(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 
                            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Keep current activity</option>
                    {activities.filter(activity => activity.is_active).map(activity => (
                      <option key={activity.id} value={activity.name}>{activity.name}</option>
                    ))}
                  </select>
                </div>
                
                {/* Business Unit Selection */}
                <div>
                  <label htmlFor="businessUnit" className="block text-sm font-medium text-slate-300 mb-2">
                    Business Unit (optional)
                  </label>
                  <select
                    id="businessUnit"
                    value={selectedBusinessUnit}
                    onChange={(e) => setSelectedBusinessUnit(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 
                            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Keep current business unit</option>
                    {businessUnits.filter(unit => unit.is_active).map(unit => (
                      <option key={unit.id} value={unit.identifier}>{unit.name} - {unit.identifier}</option>
                    ))}
                  </select>
                </div>
                
                {/* Project Selection */}
                <div>
                  <label htmlFor="project" className="block text-sm font-medium text-slate-300 mb-2">
                    Project (optional)
                  </label>
                  <select
                    id="project"
                    value={selectedProject}
                    onChange={(e) => setSelectedProject(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 
                            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Keep current project</option>
                    {projects.map(project => (
                      <option key={project.id} value={project.name}>{project.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="mt-6 flex justify-between items-center">
                <button
                  onClick={handleRemoveFromProject}
                  disabled={loading || !selectedMembers[selectedGroupForAction] || selectedMembers[selectedGroupForAction].length === 0}
                  className={`px-4 py-2 bg-red-600 text-white 
                           rounded-md transition-colors flex items-center mr-4 ${
                             loading || !selectedMembers[selectedGroupForAction] || selectedMembers[selectedGroupForAction].length === 0 
                               ? 'opacity-50 cursor-not-allowed' 
                               : 'hover:bg-red-700'
                           }`}
                >
                  {loading ? (
                    <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 718-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <X className="w-4 h-4 mr-2" />
                  )}
                  Remove Project
                </button>

                <div className="flex space-x-3 flex-shrink-0">
                  <button
                    onClick={() => setShowModifyModal(false)}
                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 
                             rounded-md transition-colors"
                  >
                    Cancel
                  </button>
                <button
                  onClick={handleModifyAssignment}
                  disabled={loading || (!selectedActivity && !selectedBusinessUnit && !selectedProject)}
                  className={`px-4 py-2 bg-blue-600 text-white 
                           rounded-md transition-colors flex items-center ${
                             loading || (!selectedActivity && !selectedBusinessUnit && !selectedProject) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'
                           }`}
                >
                  {loading ? (
                    <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <Check className="w-4 h-4 mr-2" />
                  )}
                  Apply Changes
                </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
};

export default Command;
